/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  НОРМАЛИЗАЦИЯ ОШИБОК: что угодно → `ApiError`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Из `lib/http.ts` наружу не выходит ни одна не-`ApiError`. Сторы, композаблы
 * и вью ловят ровно один тип и ветвятся ровно по одному полю — `reason`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ГЛАВНОЕ ПРАВИЛО: `message` С ПРОВОДА ПОЛЬЗОВАТЕЛЮ НЕ ПОКАЗЫВАЕТСЯ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Конверт kratos — `{code, reason, message, metadata}`. Его `message` —
 * **английский текст для разработчика**: «failed to update session: pq:
 * duplicate key value violates unique constraint». Показать его пользователю
 * значит одновременно испортить UX и слить внутренности схемы.
 *
 * Поэтому `ApiError.message` (то, что уедет в тост) собирается **здесь**, из
 * `reason`, а серверный текст откладывается в `devMessage` — для консоли и
 * багрепортов. Цепочка фолбэков:
 *
 *     спец-случай (`WEAK_PASSWORD.rule`, `RATE_LIMITED.retry_after_s`,
 *                  `IMPORT_FILE_TOO_LARGE.max_bytes`)
 *       → текст по `reason`
 *         → текст по HTTP-статусу      (сюда попадают ответы не от kratos:
 *                                       502 от nginx, 500 от прокси Vite)
 *           → общий текст
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ОДИН ТЕКСТ, КОТОРЫЙ ЛЕГКО НАПИСАТЬ НЕПРАВИЛЬНО: `EVENT_NOT_FOUND`
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Чужое мероприятие отдаёт **404, а не 403** — намеренно: 403 подтверждал бы,
 * что такой id существует, и превращал бы перебор id в перепись мероприятий
 * системы. Значит, `EVENT_NOT_FOUND` приходит в двух разных жизненных
 * ситуациях: «id опечатка» и «вас не добавили в состав».
 *
 * Текст ниже поэтому НЕ говорит «доступ запрещён» (это раскрыло бы ровно то,
 * что бэкенд прячет) и НЕ говорит просто «не найдено» (человек, которого не
 * добавили, будет искать несуществующую опечатку). Он называет оба варианта.
 */

import axios from 'axios'

import type { ErrorResponse, WeakPasswordRule } from '@/types/api'

/* ═════════════════════════════════════════════════════════════════════════
   1. ЛОКАЛЬНЫЕ `reason`, КОТОРЫХ НЕТ НА ПРОВОДЕ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Причины, которые придумывает клиент: сервер до них не доехал или не ответил.
 * Живут в том же пространстве `reason`, чтобы у вызывающего кода был **один**
 * `switch`, а не «сначала проверь, не сетевая ли это ошибка».
 *
 * • `NETWORK`          — ответа нет вообще (сервер лежит, DNS, обрыв, CORS-preflight).
 * • `TIMEOUT`          — не уложились в `timeout` axios.
 * • `CANCELED`         — `AbortController.abort()`; **не ошибка**, см. текст ниже.
 * • `NO_REFRESH_TOKEN` — refresh нечем делать: в хранилище пусто.
 */
export const LOCAL_ERROR_REASONS = ['NETWORK', 'TIMEOUT', 'CANCELED', 'NO_REFRESH_TOKEN'] as const
export type LocalErrorReason = typeof LOCAL_ERROR_REASONS[number]

/* ═════════════════════════════════════════════════════════════════════════
   2. СЛОВАРИ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * `reason` → текст для пользователя.
 *
 * Карта намеренно `Record<string, string>`, а не `Record<ErrorReason, string>`:
 * фронт старше или новее бэкенда ровно на один деплой, и неизвестный ключ
 * обязан давать общий текст, а не `undefined` в интерфейсе. Тотальность здесь
 * обеспечивается фолбэком, а не типом.
 */
const REASON_TEXT: Record<string, string> = {
  /* — 32 значения контракта `admin.v1.ErrorReason` — */
  UNKNOWN: 'Внутренняя ошибка сервера. Попробуйте позже.',
  VALIDATION_FAILED: 'Проверьте правильность заполнения полей.',
  INVALID_CREDENTIALS: 'Неверный логин или пароль.',
  ACCESS_TOKEN_MISSING: 'Требуется вход в систему.',
  ACCESS_TOKEN_INVALID: 'Сессия недействительна. Войдите заново.',
  ACCESS_TOKEN_EXPIRED: 'Сессия истекла. Войдите заново.',
  REFRESH_TOKEN_INVALID: 'Сессия недействительна. Войдите заново.',
  REFRESH_TOKEN_EXPIRED: 'Срок сессии истёк. Войдите заново.',
  REFRESH_TOKEN_REUSED: 'Сессия завершена: токен доступа был использован повторно. Войдите заново.',
  SESSION_REVOKED: 'Сессия завершена. Войдите заново.',
  SESSION_NOT_FOUND: 'Сессия не найдена.',
  FORBIDDEN: 'Недостаточно прав для этого действия.',
  USER_NOT_FOUND: 'Пользователь не найден.',
  EMAIL_ALREADY_EXISTS: 'Пользователь с таким email уже существует.',
  USERNAME_ALREADY_EXISTS: 'Пользователь с таким логином уже существует.',
  USER_DISABLED: 'Учётная запись отключена. Обратитесь к администратору.',
  WRONG_CURRENT_PASSWORD: 'Текущий пароль указан неверно.',
  WEAK_PASSWORD: 'Пароль не соответствует требованиям безопасности.',
  CANNOT_MODIFY_SELF: 'Нельзя выполнить это действие над собственной учётной записью.',
  RATE_LIMITED: 'Слишком много попыток. Попробуйте позже.',

  /** См. большой комментарий в шапке: 404 здесь означает и «нет», и «не ваше». */
  EVENT_NOT_FOUND: 'Мероприятие не найдено или вы не входите в его состав.',
  EVENT_MEMBER_NOT_FOUND: 'Пользователь не входит в состав мероприятия.',
  EVENT_MEMBER_ALREADY_EXISTS: 'Пользователь уже в составе мероприятия. Измените его роль.',
  LAST_ADMIN: 'Это последний активный администратор — его нельзя понизить, отключить или удалить.',

  PARTICIPANT_ROLE_NOT_FOUND: 'Роль участника не найдена в этом мероприятии.',
  PARTICIPANT_ROLE_ALREADY_EXISTS: 'Роль участника с таким названием уже есть в этом мероприятии.',
  PARTICIPANT_NOT_FOUND: 'Участник не найден в этом мероприятии.',
  PARTICIPANT_NUMBER_NOT_FOUND: 'Участника с таким номером в этом мероприятии нет.',
  INVALID_QR_PAYLOAD: 'QR-код не распознан как код участника. Отсканируйте ещё раз.',
  PARTICIPANT_FIELD_NOT_FOUND: 'Дополнительное поле не найдено в этом мероприятии.',
  /**
   * ⚠️ ЭТО ФОЛБЭК БЕЗ `metadata`, А НЕ ОСНОВНОЙ ТЕКСТ. Название поля уникально
   * НЕ в мероприятии, а в области видимости (роль либо «общие»), поэтому
   * «уже есть в мероприятии» здесь было бы неправдой: у роли «Тренер»
   * «Взвешивание» может существовать законно и не мешать роли «Спортсмен».
   * Что именно столкнулось, говорит `metadata.conflict_scope` — см.
   * `participantFieldConflictText()`.
   */
  PARTICIPANT_FIELD_ALREADY_EXISTS: 'Поле с таким названием уже есть.',

  ACCESS_ZONE_NOT_FOUND: 'Зона доступа не найдена в этом мероприятии.',
  ACCESS_ZONE_ALREADY_EXISTS: 'Зона с таким названием уже есть в этом мероприятии.',
  /**
   * Текст называет ПОСЛЕДСТВИЕ, а не факт занятости, и это не украшение.
   * Триграмма — единственное, что человек на входе читает с бейджа; две зоны с
   * одной меткой означают, что решение «пускать или нет» принять нельзя.
   * Организатор, который видит только «занято», склонен дописать букву и идти
   * дальше — а увидев, чем это кончится, идёт смотреть, чья это метка.
   */
  ACCESS_ZONE_ABBREVIATION_ALREADY_EXISTS:
    'Эта триграмма уже занята другой зоной. Две зоны с одной меткой на бейдже '
    + 'различить нельзя — выберите другую.',

  ACCESS_LEVEL_NOT_FOUND: 'Уровень доступа не найден в этом мероприятии.',
  /**
   * ⚠️ Конфликт по НОМЕРУ, и текст обязан это назвать: `metadata.number` везёт
   * занятую цифру, а форма вешает эту ошибку на поле номера, не на описание.
   * «Уровень уже существует» отправило бы организатора переписывать пояснение,
   * которое ни при чём.
   */
  ACCESS_LEVEL_ALREADY_EXISTS: 'Уровень с таким номером уже есть в этом мероприятии.',

  PICTOGRAM_NOT_FOUND: 'Пиктограмма не найдена в этом мероприятии.',
  /**
   * Конфликт по НАЗВАНИЮ, без учёта регистра: «Питание» и «питание» —
   * одна запись. Текст говорит про название, потому что именно его форма и
   * подсвечивает.
   */
  PICTOGRAM_ALREADY_EXISTS: 'Пиктограмма с таким названием уже есть в этом мероприятии.',
  /**
   * Байты иконки не нашлись. Экран рисует запись БЕЗ картинки, а не «битую
   * картинку»: пиктограмма без иконки — законное состояние справочника, и
   * выглядеть эти два случая должны одинаково.
   */
  PICTOGRAM_ICON_NOT_FOUND: 'Иконка не найдена — возможно, файл удалён из хранилища.',
  PICTOGRAM_ICON_INVALID:
    'Файл не распознан как изображение. Подойдут PNG (в том числе с прозрачностью) и JPEG.',
  PICTOGRAM_ICON_TOO_LARGE: 'Файл иконки слишком большой — уменьшите его и попробуйте снова.',
  /* — справочник стран: общий для всей системы — */
  COUNTRY_NOT_FOUND: 'Страна не найдена в справочнике.',
  /**
   * Текст объясняет ПРИЧИНУ, а не констатирует отказ.
   *
   * Достижимо только для строки, заведённой в обход API прямым SQL: список
   * поставляется вместе с сервером, и восстанавливать такую строку не к чему.
   * «Не найдено» отправило бы администратора искать страну, которая у него
   * прямо на экране.
   */
  COUNTRY_NOT_IN_SEED:
    'Этой страны нет в поставляемом списке ISO, поэтому возвращать её не к чему. '
    + 'Строка заведена в обход справочника.',
  /**
   * ⚠️ Этот код НЕ должен доходить до человека при обычной работе: у 249 строк
   * из 249 своего флага нет, и отсутствие загруженного файла — штатное
   * состояние, при котором рисуется флаг из поставляемого набора. Текст нужен
   * на случай, когда его показали ошибочно, и он должен об этом сказать.
   */
  COUNTRY_FLAG_NOT_FOUND: 'У страны нет загруженного флага — печатается стандартный.',
  COUNTRY_FLAG_INVALID: 'Файл не распознан как изображение. Нужен JPEG или PNG.',
  COUNTRY_FLAG_TOO_LARGE: 'Изображение слишком большое.',

  IMPORT_FILE_INVALID: 'Файл не удалось разобрать. Проверьте формат и колонки.',
  IMPORT_FILE_TOO_LARGE: 'Файл слишком большой.',

  /* — объектное хранилище: фотографии участников и картинки бейджа — */
  PARTICIPANT_PHOTO_NOT_FOUND: 'У участника нет фотографии.',
  PARTICIPANT_PHOTO_INVALID: 'Файл не распознан как изображение. Нужен JPEG или PNG.',
  PARTICIPANT_PHOTO_TOO_LARGE: 'Изображение слишком большое.',
  /**
   * ⚠️ Текст про ХРАНИЛИЩЕ, а не про фотографии, хотя ключ называется
   * `PHOTO_...`. Причина общая: тот же 501 приходит на попытку загрузить
   * подложку бейджа, и «не настроена загрузка фотографий» в ответ на загрузку
   * подложки — это подсказка не туда.
   *
   * Текст называет и следующий шаг: это не поломка, это ненастроенная функция,
   * и чинит её администратор конфигом, а не разработчик багом.
   */
  PHOTO_STORAGE_DISABLED:
    'На этом сервере не настроено хранилище файлов — загрузка изображений недоступна. '
    + 'Обратитесь к администратору.',

  /* — шаблон бейджа — */
  /**
   * ⚠️ Формально это ошибка, по смыслу — «ещё не настроено». Экран конструктора
   * обрабатывает её САМ (показывает галерею заготовок) и до тоста не доводит;
   * текст нужен на случай, когда 404 прилетит откуда-то ещё — например при
   * попытке напечатать бейдж мероприятия, где макет не завели.
   */
  BADGE_TEMPLATE_NOT_FOUND:
    'У мероприятия ещё нет шаблона бейджа — его нужно собрать в конструкторе.',
  BADGE_ASSET_NOT_FOUND: 'Картинка бейджа не найдена в хранилище.',
  BADGE_ASSET_INVALID: 'Файл не распознан как изображение. Нужен JPEG или PNG.',
  BADGE_ASSET_TOO_LARGE: 'Изображение слишком большое.',

  /* — общая библиотека сохранённых макетов — */
  /**
   * ⚠️ Здесь, в отличие от `BADGE_TEMPLATE_NOT_FOUND`, это НАСТОЯЩАЯ ошибка, и
   * текст называет её причину, а не поломку: библиотека общая, и единственный
   * способ получить этот ответ — взять шаблон, который кто-то удалил, пока
   * галерея была открыта. Действие человека — обновить список, и текст говорит
   * именно это.
   */
  BADGE_LIBRARY_TEMPLATE_NOT_FOUND:
    'Этот шаблон уже удалён из библиотеки. Обновите список.',
  BADGE_LIBRARY_TEMPLATE_ALREADY_EXISTS:
    'Шаблон с таким названием уже есть в библиотеке — выберите другое.',

  /**
   * ⚠️ Один текст на пять разных причин — так решил сервер, и повторять это
   * решение здесь обязательно. Он не различает «кода нет», «код уже погашен»,
   * «код просрочен», «это не код» и «учётку отключили», чтобы эндпоинт не
   * работал оракулом для перебирающего. Интерфейс, который начнёт угадывать
   * причину по косвенным признакам, вернёт ровно ту утечку, которую сервер
   * закрыл.
   *
   * Действие при этом одно и то же во всех пяти случаях, поэтому текст
   * называет его, а не причину.
   */
  DEVICE_TICKET_INVALID: 'Код входа недействителен, уже использован или истёк. Попросите показать новый.',

  /* — не нормализованные бэкендом: middleware kratos выше нашего кода — */
  VALIDATOR: 'Проверьте правильность заполнения полей.',
  CODEC: 'Некорректный формат запроса.',
  UNAUTHORIZED: 'Требуется вход в систему.',

  /* — чисто клиентские — */
  NETWORK: 'Сервер недоступен. Проверьте соединение и попробуйте ещё раз.',
  TIMEOUT: 'Сервер не ответил вовремя. Попробуйте ещё раз.',
  NO_REFRESH_TOKEN: 'Сессия не найдена. Войдите заново.',

  /**
   * Пустая строка — не забытый перевод, а осознанное решение.
   *
   * Отменённый запрос (`AbortController.abort()`) — штатная часть поиска по
   * мере набора: каждое нажатие клавиши гасит предыдущий `GET`. Показать здесь
   * текст значит мигать красным тостом на каждую букву. Вызывающий код
   * проверяет `if (!error.message) return` — либо `error.isCanceled`.
   */
  CANCELED: '',
}

/**
 * HTTP-статус → текст. Второй эшелон: сюда попадает всё, что **не** конверт
 * kratos, — 502/504 от nginx, 500 от прокси Vite при мёртвом бэкенде,
 * страница-заглушка балансировщика.
 */
const STATUS_TEXT: Record<number, string> = {
  400: 'Некорректный запрос.',
  401: 'Требуется вход в систему.',
  403: 'Недостаточно прав для этого действия.',
  404: 'Запрошенные данные не найдены.',
  405: 'Операция не поддерживается.',
  409: 'Данные уже изменились. Обновите страницу и повторите.',
  413: 'Слишком большой запрос.',
  429: 'Слишком много запросов. Попробуйте позже.',
  500: 'Внутренняя ошибка сервера.',
  502: 'Сервер временно недоступен. Попробуйте позже.',
  503: 'Сервер временно недоступен. Попробуйте позже.',
  504: 'Сервер не ответил вовремя. Попробуйте ещё раз.',
}

const GENERIC_TEXT = 'Что-то пошло не так. Попробуйте ещё раз.'

/**
 * `WEAK_PASSWORD.metadata.rule` → конкретное предложение.
 *
 * Смысл в том, чтобы не показывать «пароль слишком слабый» и заставлять
 * угадывать: сервер прислал, какое именно правило нарушено. Числа
 * продублированы из серверной политики (8–128) осознанно: это текст для
 * человека, а не источник правды для валидатора.
 */
const WEAK_PASSWORD_TEXT: Record<WeakPasswordRule, string> = {
  min_length: 'Пароль должен содержать не менее 8 символов.',
  max_length: 'Пароль должен содержать не более 128 символов.',
  needs_lower: 'Пароль должен содержать хотя бы одну строчную букву.',
  needs_upper: 'Пароль должен содержать хотя бы одну заглавную букву.',
  needs_digit: 'Пароль должен содержать хотя бы одну цифру.',
  contains_login: 'Пароль не должен содержать email или имя пользователя.',
  control_chars: 'Пароль не должен содержать управляющие символы.',
}

/**
 * `reason`, которые вешаются на **поле формы**, а не на тост.
 *
 * Ровно по этому признаку `useServerErrors` решает, звать ли `setErrors()` во
 * vee-validate. Обратите внимание, что `RATE_LIMITED` сюда не входит: он про
 * попытку, а не про введённые данные. `*_ALREADY_EXISTS` справочников входят —
 * у них есть ровно одно поле, куда ошибку и вешать.
 */
const VALIDATION_REASONS: ReadonlySet<string> = new Set([
  'VALIDATION_FAILED',
  'VALIDATOR',
  'WEAK_PASSWORD',
  'WRONG_CURRENT_PASSWORD',
  'EMAIL_ALREADY_EXISTS',
  'USERNAME_ALREADY_EXISTS',
  'PARTICIPANT_ROLE_ALREADY_EXISTS',
  'PARTICIPANT_FIELD_ALREADY_EXISTS',
  // У зоны два независимо уникальных поля, и сервер отдаёт РАЗНЫЕ причины
  // ровно затем, чтобы форма подсветила то из них, которое занято.
  'ACCESS_ZONE_ALREADY_EXISTS',
  'ACCESS_ZONE_ABBREVIATION_ALREADY_EXISTS',
  // Конфликт по НОМЕРУ уровня: `metadata.number` везёт занятую цифру, и место
  // этой ошибки — под полем номера, а не под описанием.
  'ACCESS_LEVEL_ALREADY_EXISTS',
  // Конфликт по НАЗВАНИЮ пиктограммы: `metadata.name` везёт занятое название,
  // и место этой ошибки — под полем названия.
  'PICTOGRAM_ALREADY_EXISTS',
])

/* ═════════════════════════════════════════════════════════════════════════
   3. СБОРКА ТЕКСТА
   ═════════════════════════════════════════════════════════════════════════ */

/** Секунды → «Повторите через 45 с.» / «Повторите через 3 минуты». */
function formatRetryAfter(seconds: number): string {
  if (seconds < 60) return `Повторите через ${seconds} с.`
  const minutes = Math.ceil(seconds / 60)
  const noun = minutes % 10 === 1 && minutes % 100 !== 11 ? 'минуту' : 'минут'
  return `Повторите через ${minutes} ${noun}.`
}

/** Байты → «5 МБ». Для лимита из `IMPORT_FILE_TOO_LARGE.max_bytes`. */
function formatBytes(bytes: number): string {
  const mib = bytes / (1024 * 1024)
  if (mib >= 1) return `${Number(mib.toFixed(mib < 10 ? 1 : 0))} МБ`
  return `${Math.ceil(bytes / 1024)} КБ`
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  409 НА НАЗВАНИИ ДОПОЛНИТЕЛЬНОГО ПОЛЯ — ТРИ РАЗНЫХ ОТКАЗА ПОД ОДНОЙ ПРИЧИНОЙ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ Название поля уникально В ОБЛАСТИ ВИДИМОСТИ, А НЕ В МЕРОПРИЯТИИ. У роли
 * «Спортсмен» и у роли «Тренер» могут быть свои «Взвешивания» — это два разных
 * поля с двумя разными наборами значений, и это законно. Нельзя ровно там, где
 * двойник неразличим: внутри одной роли, среди общих полей и между общим полем
 * и ролевым (участник с этой ролью увидел бы в форме две одинаковые графы).
 *
 * Отсюда три отказа, и человеку в каждом нужно идти в РАЗНОЕ место. Сервер
 * различает их `metadata.conflict_scope`:
 *
 *   `scope`     — столкнулись два поля одной области: два поля этой же роли
 *                 либо два общих. Сюда же попадает срабатывание индекса БД
 *                 вместо пред-проверки (гонка двух вкладок). Лечится
 *                 переименованием того, что сохраняют, — искать негде, обе
 *                 стороны конфликта на экране;
 *   `shared`    — сохраняют поле РОЛИ, а название занято ОБЩИМ полем. Искать
 *                 надо в общих полях; по ролям человек будет ходить впустую,
 *                 потому что в своей роли двойника не увидит;
 *   `otherRole` — сохраняют ОБЩЕЕ поле, а название занято полем какой-то роли.
 *                 Её id приезжает в `metadata.conflict_role_id`; ИМЯ роли сервер
 *                 намеренно не шлёт — скопированное в ошибку имя протухает при
 *                 первом же переименовании роли, а справочник ролей у клиента
 *                 и так есть.
 *
 * Поэтому имя роли — параметр, а не то, что функция резолвит сама: `api-error`
 * лежит ниже сторов и знать про Pinia не может. Общий путь (тост из `http`)
 * зовёт её без имени и получает текст «занято полем одной из ролей»; диалог
 * поля, у которого стор ролей под рукой, зовёт с именем и называет роль.
 *
 * ⚠️ Пустое имя роли — ШТАТНО: роль могли удалить, пока экран был открыт.
 * Тогда текст остаётся безымянным. Сырой uuid не показывается никогда: он
 * ничего не говорит человеку и выглядит как сбой.
 */
export function participantFieldConflictText(
  metadata: Record<string, string>,
  roleName = '',
): string {
  // Ключи camelCase — исключение из общего snake_case у `metadata` (см. таблицу
  // в `types/api.ts`). Snake-написание принимается на случай, если сервер
  // однажды приведёт их к общему виду: разойтись на одном деплое здесь значит
  // молча вернуться к общему тексту.
  const scope = metadata.conflict_scope ?? ''

  if (scope === 'shared') {
    return 'Такое название занято общим полем — оно применяется ко всем ролям, '
      + 'поэтому у этой роли такая графа уже есть. Выберите другое название.'
  }

  if (scope === 'otherRole') {
    const whose = roleName ? `полем роли «${roleName}»` : 'полем одной из ролей'
    return `Такое название занято ${whose}. Общее поле применяется ко всем ролям, `
      + 'поэтому оно столкнулось бы с ним. Выберите другое название.'
  }

  if (scope === 'scope') {
    return 'Поле с таким названием уже есть — у этой же роли либо среди общих '
      + 'полей. Выберите другое название.'
  }

  return REASON_TEXT.PARTICIPANT_FIELD_ALREADY_EXISTS
}

/**
 * Спец-случаи, где `metadata` делает текст точнее. Возвращает `null`, если
 * уточнять нечем, — и тогда работает обычная цепочка фолбэков.
 */
function specificText(reason: string, metadata: Record<string, string>): string | null {
  if (reason === 'WEAK_PASSWORD') {
    const rule = metadata.rule as WeakPasswordRule | undefined
    return (rule && WEAK_PASSWORD_TEXT[rule]) || null
  }

  if (reason === 'RATE_LIMITED') {
    // Значения `metadata` — всегда строки, даже числовые.
    const seconds = Number(metadata.retry_after_s)
    if (Number.isFinite(seconds) && seconds > 0) {
      return `${REASON_TEXT.RATE_LIMITED} ${formatRetryAfter(Math.ceil(seconds))}`
    }
    return null
  }

  /**
   * Все «слишком большой файл» с одинаковой обработкой: у каждого в
   * `metadata.max_bytes` лежит лимит, и без него текст бесполезен — «файл
   * слишком большой» не говорит человеку, до чего его ужимать.
   *
   * Список перечислен, а не сведён к `endsWith('_TOO_LARGE')`: суффикс — это
   * совпадение написаний, а договор про `max_bytes` даёт КАЖДАЯ причина
   * отдельно. Причина, у которой нет строки в `REASON_TEXT`, напечатала бы
   * здесь «undefined Максимум — 10 МиБ» — поэтому добавлять сюда причину можно
   * только вместе с её текстом выше.
   */
  if (
    reason === 'IMPORT_FILE_TOO_LARGE'
    || reason === 'BADGE_ASSET_TOO_LARGE'
    || reason === 'COUNTRY_FLAG_TOO_LARGE'
    || reason === 'PICTOGRAM_ICON_TOO_LARGE'
  ) {
    const maxBytes = Number(metadata.max_bytes)
    if (Number.isFinite(maxBytes) && maxBytes > 0) {
      return `${REASON_TEXT[reason]} Максимум — ${formatBytes(maxBytes)}.`
    }
    return null
  }

  /**
   * `metadata.detail` у разбора файла — это **русский текст от сервера**
   * («нет колонки «Фамилия»»), а не английский `message` для разработчика:
   * его пишет `participantimport`, и он единственное, что объясняет оператору,
   * чем именно плох его файл.
   */
  if (reason === 'IMPORT_FILE_INVALID') {
    const detail = metadata.detail?.trim()
    return detail ? `${REASON_TEXT.IMPORT_FILE_INVALID} ${detail}` : null
  }

  /**
   * Три разных отказа под одной причиной — см. `participantFieldConflictText`.
   * Здесь имя роли неизвестно (сторов на этом уровне нет), и текст остаётся
   * безымянным; диалог поля перезовёт ту же функцию с именем.
   */
  if (reason === 'PARTICIPANT_FIELD_ALREADY_EXISTS') {
    return participantFieldConflictText(metadata)
  }

  return null
}

/** Полная цепочка: спец-случай → reason → статус → общий текст. */
function describe(reason: string, status: number, metadata: Record<string, string>): string {
  const specific = specificText(reason, metadata)
  if (specific !== null) return specific

  // `??`, а не `||`: у `CANCELED` текст — пустая строка, и она значащая.
  return REASON_TEXT[reason] ?? STATUS_TEXT[status] ?? GENERIC_TEXT
}

/* ═════════════════════════════════════════════════════════════════════════
   4. КЛАСС
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Единственный тип ошибки, который видит приложение.
 *
 * Поля присваиваются в теле конструктора, а не через параметры-свойства:
 * `erasableSyntaxOnly: true` в `tsconfig.app.json` запрещает конструкцию
 * `constructor(public status: number)` — у неё есть рантайм-семантика,
 * несводимая к стиранию типов.
 */
export class ApiError extends Error {
  /** HTTP-статус. `0` — ответа не было вообще (`NETWORK` / `TIMEOUT` / `CANCELED`). */
  status: number

  /** Ключ ветвления и локализации. Не обязан входить в `ErrorReason` — см. шапку. */
  reason: string

  /** `map<string,string>` из конверта. Всегда объект, в худшем случае пустой. */
  metadata: Record<string, string>

  /**
   * Серверный `message` — **английский, для разработчика**.
   * Для логов и багрепортов; в интерфейс не попадает никогда.
   */
  devMessage: string

  constructor(
    status: number,
    reason: string,
    metadata: Record<string, string> = {},
    devMessage = '',
  ) {
    super(describe(reason, status, metadata))

    this.name = 'ApiError'
    this.status = status
    this.reason = reason
    this.metadata = metadata
    this.devMessage = devMessage
  }

  /**
   * Ошибка относится к введённым данным ⇒ её место под полем формы, а не в
   * тосте (`useServerErrors` вешает её через `setErrors()`).
   */
  get isValidation(): boolean {
    return VALIDATION_REASONS.has(this.reason)
  }

  /**
   * Запрос отменён вызывающим кодом. Не ошибка: `message` пустой, показывать
   * нечего. Вынесено в геттер, чтобы вызывающий код не сравнивал строки руками.
   */
  get isCanceled(): boolean {
    return this.reason === 'CANCELED'
  }

  /**
   * Мероприятие «не существует» — либо его правда нет, либо нас нет в его
   * составе. Отдельный геттер, потому что на это ветвятся сразу три места:
   * стор мероприятия (уводить на список), карточка участника и импорт.
   */
  get isEventGone(): boolean {
    return this.reason === 'EVENT_NOT_FOUND'
  }
}

/* ═════════════════════════════════════════════════════════════════════════
   5. НОРМАЛИЗАЦИЯ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Похоже ли `data` на конверт ошибки kratos.
 *
 * Проверяем именно `reason`, а не `code`: `code` есть и у половины чужих
 * JSON-ответов, а `reason: string` — подпись конкретно нашего конверта.
 */
function isErrorEnvelope(data: unknown): data is Partial<ErrorResponse> {
  return typeof data === 'object' && data !== null && typeof (data as ErrorResponse).reason === 'string'
}

/**
 * `metadata` в конверте — protobuf `map`, то есть по контракту присутствует
 * всегда (`{}`, когда пусто). Здесь мы всё равно приводим её к объекту: этот
 * код обрабатывает и ответы, которые kratos не писал (502 от nginx,
 * JSON-заглушка балансировщика), и ломаться на них он не имеет права.
 */
function toMetadata(value: unknown): Record<string, string> {
  if (typeof value !== 'object' || value === null) return {}

  const result: Record<string, string> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'string') result[key] = item
    else if (typeof item === 'number' || typeof item === 'boolean') result[key] = String(item)
  }
  return result
}

/**
 * Что угодно → `ApiError`. Идемпотентна: `normalizeError(apiError) === apiError`.
 *
 * Порядок веток — от самого специфичного к самому общему, и он значим:
 * у отменённого запроса `response` тоже отсутствует, так что проверка
 * `ERR_CANCELED` обязана идти **до** проверки на сетевую ошибку, иначе каждое
 * нажатие клавиши в поиске давало бы «Сервер недоступен».
 */
export function normalizeError(err: unknown): ApiError {
  if (err instanceof ApiError) return err

  if (axios.isAxiosError(err)) {
    // 1. Отмена — не ошибка. Должна идти первой: `response` у неё тоже нет.
    if (err.code === 'ERR_CANCELED') {
      return new ApiError(0, 'CANCELED', {}, err.message)
    }

    // 2. Не уложились в `timeout`. `ETIMEDOUT` — тот же случай под Node.
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      return new ApiError(0, 'TIMEOUT', {}, err.message)
    }

    const response = err.response

    // 3. Ответа не было вообще: сервер лежит, обрыв, DNS, зарубленный preflight.
    if (!response) {
      return new ApiError(0, 'NETWORK', {}, err.message)
    }

    // 4. Ответ есть. Конверт kratos — если это он.
    const data: unknown = response.data
    if (isErrorEnvelope(data)) {
      return new ApiError(
        // `code` в конверте равен HTTP-статусу, но авторитет — сам статус:
        // на пути к браузеру ответ мог переписать прокси.
        response.status,
        data.reason ?? 'UNKNOWN',
        toMetadata(data.metadata),
        typeof data.message === 'string' ? data.message : '',
      )
    }

    // 5. Ответ не от kratos. `reason` пустой — намеренно: сказать «UNKNOWN»
    //    значило бы приписать серверу причину, которой он не называл, и
    //    перебить более точный текст по статусу (502 → «Сервер временно
    //    недоступен» вместо «Внутренняя ошибка сервера»).
    return new ApiError(response.status, '', {}, typeof data === 'string' ? data : err.message)
  }

  // 6. Не axios: `AbortError` от голого fetch, TypeError из нашего же кода.
  if (err instanceof DOMException && err.name === 'AbortError') {
    return new ApiError(0, 'CANCELED', {}, err.message)
  }

  if (err instanceof Error) {
    return new ApiError(0, 'UNKNOWN', {}, err.message)
  }

  return new ApiError(0, 'UNKNOWN', {}, String(err))
}

/**
 * ⚠️ ОСОБЫЙ СЛУЧАЙ: ошибка, приехавшая при `responseType: 'blob'`.
 *
 * Скачивание шаблона импорта просит у axios `Blob`, и axios отдаёт `Blob` **и
 * на ошибку тоже**. `normalizeError` в таком теле конверт kratos не узнаёт и
 * даёт текст по HTTP-статусу — «Слишком большой запрос» вместо
 * `PARTICIPANT_FIELD_NOT_FOUND`.
 *
 * Эта функция дочитывает `Blob` и нормализует ещё раз. Асинхронная (чтение
 * blob'а асинхронно по природе), поэтому вызывается точечно там, где ответ
 * бинарный, а не из интерцептора.
 */
export async function normalizeBlobError(err: unknown): Promise<ApiError> {
  const error = normalizeError(err)

  const data: unknown = axios.isAxiosError(err) ? err.response?.data : undefined
  if (!(data instanceof Blob)) return error

  try {
    const parsed: unknown = JSON.parse(await data.text())
    if (isErrorEnvelope(parsed)) {
      return new ApiError(
        error.status,
        parsed.reason ?? 'UNKNOWN',
        toMetadata(parsed.metadata),
        typeof parsed.message === 'string' ? parsed.message : '',
      )
    }
  } catch {
    /* не JSON — оставляем текст по статусу, он честнее выдуманной причины */
  }

  return error
}
